import { useState, useEffect, useCallback, useRef } from "react";
import '../../css/index.css';
import HttpService from "../../services/HttpService";
import { useTranslation } from 'react-i18next';
import SchemaSet from "./Components/SchemaSet";
import Select from "react-select";
import Button from "../Tools/Components/Button";

const ValidatorPagina = ({ location }) => {
	const [dependencyGroup, setDependencyGroup] = useState(null);
	const [selectedSetName, setSelectedSetName] = useState("");
	const [selectedSetVersion, setSelectedSetVersion] = useState("production");
	const [versions, setVersions] = useState([]);
	const [input, setInput] = useState("");
	const [showResults, setShowResults] = useState(false);
	const [validationResults, setValidationResults] = useState({ valid: false, errors: ["no results loaded yet"] });
	const [highlights, setHighlights] = useState("");

	const { t } = useTranslation();

	const axiosRef = useRef(null);
	const highlightRef = useRef(null);

	const createHighlightElements = (highlights) => {
		highlights = [...highlights].sort((a, b) => a.start - b.start);
		const elements = [];
		let currentIndex = 0;

		highlights.forEach((highlight, index) => {
			const start = Math.max(0, highlight.start);
			const end = Math.min(input.length, highlight.end);
			console.log("start: " + start +  " end: " + end);
			// Text before this highlighted range
			if (start > currentIndex) {
				elements.push(
					<span key={`text-${index}`}>
						{input.slice(currentIndex, start)}
					</span>
				);
			}

			// Highlighted range
			if (end > start) {
				elements.push(
					<span
						key={`highlight-${index}`}
						style={{
							backgroundColor: highlight.colour ?? "yellow",
							color: "transparent",
						}}
					>
						{input.slice(start, end)}
					</span>
				);
			}

			currentIndex = Math.max(currentIndex, end);
		});

		// Remaining text
		if (currentIndex < input.length) {
			elements.push(
				<span key="remaining">
					{input.slice(currentIndex)}
				</span>
			);
		}

		return elements;
	};


	const handleSelectSet = useCallback(() => {
		if (selectedSetName === "") return;
		const axios = axiosRef.current;
		axios.get(process.env.REACT_APP_SD_VALIDATOR_URL + "SchemaSet/all/" + dependencyGroup.name + "/" + selectedSetName)
			.then(response => setVersions(response.data.map(setVersion => { return { label: setVersion.version, value: setVersion.version } })))
			.catch(ex => console.log("an error occurred while fetching available versions: " + ex.message));
	}, [dependencyGroup, selectedSetName]);

	useEffect(() => {
		handleSelectSet();
	}, [handleSelectSet, dependencyGroup, selectedSetName])

	const handleRunValidator = () => {
		const axios = axiosRef.current;
		console.log(input.length);
		axios.post(process.env.REACT_APP_SD_VALIDATOR_URL + "Validate/" + dependencyGroup.name + '/' + selectedSetName + '?version=' + selectedSetVersion, input.replace(/\r\n|\r/g, '\n'), { headers: { "Content-Type": "text/plain" } })
			.then(response => {
				setValidationResults(response.data);
				var errorHighlights = response.data.errors.filter(er => er.errorStartCharIndex !== null).map(er => ({
					start: er.errorStartCharIndex,
					end: er.errorEndCharIndex ?? er.errorStartCharIndex + 1,
					colour: "red"
				}));
				var warningHighlights = response.data.warnings.filter(wa => wa.errorStartCharIndex !== null).map(wa => ({
					start: wa.errorStartCharIndex,
					end: wa.errorEndCharIndex ?? wa.errorStartCharIndex + 1,
					colour: "#7b7c2d"
				}));
				setHighlights(createHighlightElements(errorHighlights.concat(warningHighlights)));
				setShowResults(true);
			})
			.catch((ex) => console.log("error while trying to connect to the validator: " + ex.message));
	};

	useEffect(() => {
		const httpService = HttpService;
		httpService.configure();
		axiosRef.current = httpService.getAxiosClient();

		if (location !== undefined && location.dependencyGroup) {
			setDependencyGroup(location.dependencyGroup);
			return;
		}

		const axios = axiosRef.current;
		if (!axios) return;
		const groupName = window.location.href.substring(window.location.href.lastIndexOf('/') + 1);
		axios.get(process.env.REACT_APP_SD_VALIDATOR_URL + "DependencyGroup/" + groupName)
			.then(response => {
				setDependencyGroup(response.data);
				if (response.data.dependants.length > 0) setSelectedSetName(response.data.dependants[0].name);
			})
			.catch(ex => { console.log("error while trying to load dependency group values: " + ex.message) });
	}, [location]);


	return (
		<div className="relative size-full">
			<div className="flex flex-row size-full gap-5">
				{/* MAIN CONTENT */}
				<div className="flex flex-col items-center size-full py-2">

					{/* HEADER */}
					<div className="bg-[#00aaad] dark:bg-[#008b8e] w-full flex justify-center py-[10px] border-2 border-[#b0b6c1]">
						<div className="font-bold text-[20px] py-3">
							{(dependencyGroup?.name ?? "") + " " + t("validator.header")}
						</div>
					</div>

					{/* ContentBox */}
					<div className="flex flex-col bg-[rgba(4, 5, 7, 0.75)] dark:bg-[rgba(55,65,81,0.75)] w-full border-2 border-[#b0b6c1]">

						<div className="flex flex-row justify-between items-center px-[28px] py-[10px] gap-[10px]">
							<Button text={t("validator.editSelectedSetBtn")} onClick={"/CreateValidatorSet/" + (dependencyGroup?.name ?? "") + "/" + selectedSetName} />
							<label>{selectedSetName === "" ? "" : t("validator.selectedSetLabel") + " " + selectedSetName}</label>
							<div className="flex flex-row items-center gap-[5px]">
								<label>{t("validator.selectedVersionLabel")}</label>
								<Select
									options={versions}
									value={versions.find((ver) => ver.value === selectedSetVersion)}
									onChange={(option) => setSelectedSetVersion(option.value)}
								/>
							</div>
						</div>

						<div className="flex flex-row items-top px-[28px] py-[10px] gap-[10px]">
							{/* SchemaSet Table */}
							<div className="flex flex-col w-[30%]">
								<div className="bg-[rgba(58,173,255,0.5)] dark:bg-[rgba(101,191,255,0.5)] h-[35px] flex items-center px-[10px] font-bold border border-black">
									<div className="flex-1">{t("validator.schemaSetNameTableHeader")}</div>
									<div className="flex-1">{t("validator.schemaSetTopSchemaCount")}</div>
								</div>

								{(dependencyGroup?.dependants ?? []).map((schemaSet, index) => (
									<SchemaSet
										key={schemaSet.name}
										callback={() => {
											if (dependencyGroup.dependants.length <= index) {
												console.log("index '" + index + "' is out of bounds");
												console.log(dependencyGroup);
												return;
											}
											setSelectedSetName(dependencyGroup.dependants[index].name);
										}}
										schemaSet={schemaSet}
										index={index}
									/>
								))}
								<div className="h-[35px] flex px-[10px]" />
								<div className="flex items-center justify-center">
									<Button text={t("validator.createSetBtn") + ": " + (dependencyGroup?.name ?? "")} onClick={() => window.location.assign("/CreateValidatorSet/" + dependencyGroup.name)} />
								</div>
							</div>
							{/*input text box*/}
							<div className="flex flex-col items-center bg-white dark:bg-gray-700 h-[90%] w-[70%] relative gap-[2%]">
								<div className="overflow-clip rounded-[inherit] size-full">
									<div
										style={{
											position: "relative",
											width: "100%",
											height: "1000px",
										}}
									>
										{/* Highlight layer */}
										<div
											ref={highlightRef}
											aria-hidden="true"
											style={{
												position: "absolute",
												inset: 0,
												padding: "5px",
												boxSizing: "border-box",

												// Must match textarea text styling
												font: "inherit",
												lineHeight: "inherit",
												letterSpacing: "inherit",
												whiteSpace: "pre-wrap",
												overflow: "hidden",

												pointerEvents: "none",
												color: "transparent",
											}}
										>
											{highlights}
										</div>

										{/* Actual textarea */}
										<textarea
											id="validatorTextBox"
											className="whitespace-prewrap overflow-x-auto h-[1000px] w-full p-[5px] bg-transparent text-black dark:text-white resize-none"
											style={{
												position: "relative",
												zIndex: 1,
												background: "transparent",
												color: "inherit",
											}}
											placeholder={t("validator.inputPlaceholder")}
											value={input}
											onChange={(event) => {
												setHighlights("");
												setInput(event.target.value);
											}}
											onScroll={(event) => {
												if (highlightRef.current) {
													highlightRef.current.scrollLeft = event.target.scrollLeft;
													highlightRef.current.scrollTop = event.target.scrollTop;
												}
											}}
										/>
									</div>
								</div>
								<div className="absolute border border-black inset-0 pointer-events-none" />
								<Button text={t("validator.validateBtn")} onClick={() => handleRunValidator()} />
							</div>
						</div>
					</div>

				</div>
			</div>
			{showResults ?
				(
					<div className="flex flex-col items-center gap-[10px] fixed inset-0 justify-center bg-black/40 dark:bg-black/70"
						style={{
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							zIndex: 9999,
							padding: "20px",
							borderRadius: "8px",
							minWidth: "80%",
							minHeight: "80%"
						}}
					>
						<textarea className="whitespace-nowrap overflow-x-auto bg-white text-black dark:bg-zinc-900 dark:text-white p-6 rounded-xl size-full resize-none"
							value={
								"input was " + (validationResults.valid ? "valid" : "invalid") +
								(validationResults.warnings.length > 0 && validationResults.valid ? " with warnings" : "") +
								(validationResults.errors.length > 0 ? "\n\nErrors:\n - " + validationResults.errors.map(errorObj => errorObj.message.trim().replace(/\n\s+/g, " ").replace(/\n\s*\n+/g, "\n").replaceAll('\n', '\n\t - ')).join("\n\n - ") : "") +
								(validationResults.warnings.length > 0 ? "\n\nWarnings:\n - " + validationResults.warnings.map(warningObj => warningObj.message.trim().replace(/\n\s+/g, " ").replace(/\n\s*\n+/g, "\n").replaceAll('\n', '\n\t - ')).join("\n\n - ") : "")
							}
							readOnly
						/>
						<Button text={t("validator.closeFeedbackBtn")} onClick={() => setShowResults(false)} />
					</div>
				)
				:
				("")
			}
		</div>
	);
};

export default ValidatorPagina;